import {
  CheckOutlineIcon,
  ChevronDownOutlineIcon,
  Icon,
  iconVariants,
} from '@fountain/ripple/icon';
import { Button, Menu, MenuItem } from '@material-ui/core';
import classNames from 'classnames';
import React, { useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useStyles } from './styles';

export interface QueueFilterOption<T extends string> {
  value: T;
  label: MessageDescriptor;
}

interface QueueFilterMenuProps<T extends string> {
  /** Shown on the button while nothing is filtered — "Type", "Status". */
  label: MessageDescriptor;
  /** The value meaning "no filter", which is a real, selectable option. */
  allValue: T;
  options: Array<QueueFilterOption<T>>;
  value: T;
  onChange: (next: T) => void;
  /** Held while a write is in flight that a filter change would strand. */
  disabled?: boolean;
  testId?: string;
}

/**
 * One of the queue's dropdown filters (Design Requirements §5).
 *
 * ## The button carries the selection
 *
 * The design draws a fixed "Type ⌄" with the chosen entry marked inside the
 * menu. That leaves a filtered list looking like the whole list — the only thing
 * distinguishing "every type" from "offers only" is a popover nobody has open.
 * `constraint-anti-pattern-guard` prohibits displaying partial data without
 * indicating it is partial, so the button reads "Offer" once one is chosen and
 * the tint reinforces it rather than being the signal.
 *
 * ## Not a `Select`
 *
 * A native-backed `Select` renders a bordered 48px field with a floating label:
 * the wrong control beside a 32px toggle pill, and the wrong shape for something
 * whose resting state names a category rather than a value. `Button` + `Menu` is
 * what the sibling rules tab's create control already uses, and
 * `aria-haspopup="menu"` with `aria-expanded` is what says the caret opens
 * something.
 *
 * `aria-controls` is set only while open: `Menu` unmounts its popover on close
 * (`keepMounted` is off), so a static one would name an element that is gone.
 */
export function QueueFilterMenu<T extends string>({
  label,
  allValue,
  options,
  value,
  onChange,
  disabled = false,
  testId,
}: QueueFilterMenuProps<T>) {
  const styles = useStyles();
  const intl = useIntl();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const filtered = value !== allValue;
  const selected = options.find(option => option.value === value);
  // Falls back to the group label when the value names an option this build no
  // longer has — a button reading "Type" is honest about being unfiltered in a
  // way a blank one is not.
  const buttonLabel = filtered && selected ? selected.label : label;
  const menuId = `approvals-queue-filter-${String(label.id)}`;
  // The one row in the tab order, which `MenuList` would otherwise pick for
  // itself: the chosen entry, or the first when the value names an option this
  // build no longer has. Written out because it only picks for a child that
  // passes no `tabIndex`, and every child below passes one.
  const tabStopIndex = Math.max(
    options.findIndex(option => option.value === value),
    0,
  );

  return (
    <>
      <Button
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={Boolean(anchor)}
        aria-haspopup="menu"
        className={classNames(styles.filterPill, {
          [styles.filterPillActive]: filtered,
        })}
        data-testid={testId}
        disabled={disabled}
        endIcon={
          <Icon icon={ChevronDownOutlineIcon} size="sm" color="inherit" />
        }
        variant="outlined"
        onClick={event => setAnchor(event.currentTarget)}
      >
        {intl.formatMessage(buttonLabel)}
      </Button>
      <Menu
        anchorEl={anchor}
        MenuListProps={{ 'aria-label': intl.formatMessage(label), id: menuId }}
        open={Boolean(anchor)}
        PaperProps={{ className: styles.filterMenuPaper }}
        onClose={() => setAnchor(null)}
      >
        {options.map((option, index) => (
          <MenuItem
            className={classNames(styles.filterMenuItem, {
              [styles.filterMenuItemSelected]: option.value === value,
            })}
            key={option.value}
            // One of a set where exactly one is chosen, which is what
            // `menuitemradio` means — and `aria-checked` is the only attribute
            // that carries the choice to a screen reader here. MUI's `selected`
            // prop puts a class on the row and nothing else: `aria-selected` is
            // never emitted, and `role="menuitem"` would not support it anyway.
            // Without this the weight and the tick are the whole signal, and the
            // tick is `aria-hidden`.
            aria-checked={option.value === value}
            role="menuitemradio"
            selected={option.value === value}
            // The roving tab order, kept as `MenuList` would have set it: one
            // row reachable by Tab, the rest by the arrow keys. It clones that
            // `0` onto the active row only when the child passes no `tabIndex`,
            // so naming one here means naming both.
            tabIndex={index === tabStopIndex ? 0 : -1}
            onClick={() => {
              setAnchor(null);
              onChange(option.value);
            }}
          >
            {intl.formatMessage(option.label)}
            {/* Decorative — `aria-checked` above already carries this. Rendered
                only on the chosen row, so the labels stay left-aligned. */}
            {option.value === value && (
              <CheckOutlineIcon
                aria-hidden="true"
                className={classNames(
                  iconVariants({ size: 'lg' }),
                  styles.filterMenuCheck,
                )}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
